<template>
  <v-form
    v-model="form_is_valid"
    @submit.prevent="save_changes"
  >
    <template v-for="(items, key) in edit_fields">
      <div :key="key">
        <h3
          class="text-h3 mb-8"
          v-if="items.title"
        >
          {{ items.title }}
        </h3>
        <div
          v-for="(row, i) in items.rows"
          :key="i"
          :class="[ 'itemsRow', items.class ]"
        >
          <component
            v-for="(comp, idx) in row"
            :key="idx"
            :is="component_by_type(comp.field_type)"
            v-bind="props_component(comp)"
            @change="update_field(comp.field_name, $event)"
            @input="update_field(comp.field_name, $event)"
            @date_changed="update_field(comp.field_name, $event)"
            @updated_avatar="update_field(comp.field_name, $event)"
            @[get_event_name(comp)]="get_handler(comp, $event)"
            @search_input="update_search_val_debounce($event)"
          />
        </div>
      </div>
    </template>
    <div class="itemsRow">
      <div class="item wide form-bottom">
        <v-btn
          class="mr-4"
          depressed
          color="accent darken-4"
          outlined
          type="submit"
          :disabled="!is_disabled_btn"
        >
          Сохранить изменения
        </v-btn>
        <v-btn
          text
          @click="$emit('cancel')"
        >
          Отменить
        </v-btn>
      </div>
    </div>
  </v-form>
</template>

<script lang="ts">
import { Component, Mixins, Prop } from 'vue-property-decorator';
import { EditFormRowTypes } from '@/enums';
import { EditFormRow, EditFormRows } from '@/types';
import { is_equal, debounce } from '@/utils';
import { Staffs } from '@/modules/staffs/types';
import { User } from '@/modules/user/types';
import ValidateMixin from '@/mixins/validate';
import Alert from '@/modules/staffs/components/Alert.vue';
import Checkbox from '../global/checkbox/Checkbox.vue';
import Datepicker from '../global/datepicker/Datepicker.vue';
import Select from '../global/inputs/select/Select.vue';
import TextBlock from './TextBlock.vue';
import AvatarBlock from './AvatarBlock.vue';

@Component({
  components: {
    AvatarBlock,
    TextBlock,
    Checkbox,
    Datepicker,
    Select,
    Alert,
  },
})
export default class EditFormBuilder extends Mixins(ValidateMixin) {
  @Prop({ type: Array, default: () => ([]) })
  edit_fields!: EditFormRows[];

  /**
   * Исходные данные, ДО редактирования.
   * Нужно для сравнения
   */
  @Prop({ type: Object, default: () => ({}), required: true })
  initial_obj!: Staffs.Staff | User.Profile;

  /**
   * Объект ДЛЯ редактирования, т. е. изменяем его поля
   */
  @Prop({ type: Object, default: () => ({}), required: true })
  ediatable_obj!: Staffs.Staff | User.Profile;

  form_is_valid = false;

  get original_initials() {
    const { name = '', surname = '' } = this.initial_obj;

    return `${name || ''} ${surname || ''}`;
  }

  get form_changed() {
    return !is_equal(this.ediatable_obj, this.initial_obj);
  }

  get is_disabled_btn() {
    return this.form_is_valid && this.form_changed;
  }

  component_by_type(type: EditFormRowTypes) {
    switch (type) {
      case EditFormRowTypes.AVATAR:
        return AvatarBlock;

      case EditFormRowTypes.TEXT:
        return TextBlock;

      case EditFormRowTypes.CHECKBOX:
        return Checkbox;

      case EditFormRowTypes.DATE:
        return Datepicker;

      case EditFormRowTypes.SELECT:
        return Select;

      case EditFormRowTypes.ALERT:
        return Alert;

      default:
        return TextBlock;
    }
  }

  props_component(row: EditFormRow) {
    const select_value = (row?.select && 'value' in row.select)
      ? row.select?.value
      : this.ediatable_obj[row.field_name];

    switch (row.field_type) {
      case EditFormRowTypes.AVATAR:
        return {
          avatar: this.initial_obj[row.field_name] ?? '',
          original_initials: this.original_initials,
          class: 'itemsRow mb-6',
          avatar_col_class: 'item',
        };

      case EditFormRowTypes.TEXT:
        return {
          blue_icon: true,
          require: row.is_require,
          label: row.label,
          initial_value: this.initial_obj[row.field_name], // исходное значение
          value: this.ediatable_obj[row.field_name], // значение, которое будем менять для инпута с маской
          class: `${row.wide ? 'wide' : ''} item`,
          disabled: row?.disabled || false,
          rules: this.build_rules(row),
          mask: row.mask,
          clearable: row.clearable,
        };

      case EditFormRowTypes.SELECT:
        return {
          blue_icon: true,
          require: row.is_require,
          label: row.label,
          value: select_value,
          class: `${row.wide ? 'wide' : ''} item`,
          rules: this.build_rules(row),
          items: row.select?.items,
          multiple: row.select?.is_multiple,
          clearable: row.clearable,
          loading: row.loading,
          return_object: row?.select?.return_object,
          item_value: row?.select?.item_value,
          'item-text': row?.select?.['item-text'],
          is_autocomplete: row.select?.is_autocomplete,
        };

      case EditFormRowTypes.CHECKBOX:
        return {
          label: row.label,
          class: 'item',
          inputValue: select_value,
          hideDetails: row.hide_details,
        };

      case EditFormRowTypes.DATE:
        return {
          label: row.label,
          class: 'item',
          date_prop: this.ediatable_obj[row.field_name],
          hideDetails: row.hide_details,
          max: row.max_date,
        };

      case EditFormRowTypes.ALERT:
        return {
          value: select_value,
          class: `${row.wide ? 'wide' : ''} item mb-4`,
          type: row.alert_type || 'info',
        };

      default:
        return {};
    }
  }

  build_rules(row: EditFormRow) {
    const is_require = row.is_require ? ((v: string) => this.is_require(v) || this.require_message) : true;
    const rules = row?.rules?.length ? row.rules : [];

    return [
      is_require,
      ...rules,
    ];
  }

  get_handler(row: EditFormRow, val: unknown) {
    return row?.handler ? row?.handler(val) : () => ({});
  }

  get_event_name(row: EditFormRow) {
    return row?.event_handler_name ? row.event_handler_name : '';
  }

  save_changes() {
    if (this.form_is_valid) {
      this.$emit('save');
    }
  }

  update_field(key: string, value: unknown) {
    this.$emit('update_field', { key, value });
  }

  update_search_val(value: string) {
    this.$emit('update_search_val', value);
  }

  update_search_val_debounce = debounce(this.update_search_val, 500);
}
</script>

<style lang="scss" src="@/scss/common/inputs-row.scss" />
